import React from 'react'
import { AbstractCatalogService, TCatalogType } from '../services/AbstractCatalogService'
import { dynamicCatalogService } from '../services/DynamicCatalogService'
import { singleCatalogService } from '../services/SingleCatalogService'
import { t } from '../config/i18n'
import { IBreadcrumb } from '../services/RouterService'
import { useBreadcrumbs } from './useBreadcrumbs'
import { routes } from '../config/routes'
import { generatePath, useParams } from 'react-router'

type TPage =
  |'catalog_list'
  |'catalog_config'
  |'catalog_item_list'
  |'catalog_item_detail'

interface IUseCatalogResult {
  catalogService: IUseCatalogServiceResult
  texts: IUseCatalogTextsResult
}
function useCatalog (type: string = '', page: TPage): IUseCatalogResult {
  if (!['dynamic', 'single'].includes(type)) {
    throw new Error('Wrong catalog type')
  }

  const catalogService = useCatalogService(type as TCatalogType)
  const texts = useCatalogTexts(type as TCatalogType)
  useCatalogBreadcrumbs(catalogService, type as TCatalogType, page)

  return { catalogService, texts }
}

type IUseCatalogServiceResult = AbstractCatalogService<any, any>
function useCatalogService (type: TCatalogType): IUseCatalogServiceResult {
  if (type === 'dynamic') { return dynamicCatalogService }
  if (type === 'single') { return singleCatalogService }
  throw new Error('Wrong catalog type')
}

interface IUseCatalogTextsResult {
  titleCatalogList: string
}
function useCatalogTexts (type: TCatalogType): IUseCatalogTextsResult {
  if (type === 'dynamic') {
    return {
      titleCatalogList: t.nav.dynamicCatalogs,
    }
  } else {
    return {
      titleCatalogList: t.nav.singleCatalogs,
    }
  }
}

function useCatalogBreadcrumbs (
  catalogService: AbstractCatalogService<any, any>,
  type: TCatalogType,
  page: TPage)
{
  const texts = useCatalogTexts(type)
  const params = useParams()
  const { id, itemId } = params
  const isCreate = [id, itemId].includes('new')

  const bc: IBreadcrumb[] = [
    { url: routes.home, title: t.nav.main },
  ]

  if (page === 'catalog_list') {
    bc.push({ title: texts.titleCatalogList })
  }

  if (page === 'catalog_config') {
    const config = catalogService.store.configItemDetails
    bc.push({ url: generatePath(routes.catalogList, { type }), title: texts.titleCatalogList })
    bc.push({ title: isCreate ? t.catalog.create : config.name })
  }

  if (page === 'catalog_item_list') {
    const config = catalogService.store.configItemDetails
    const path = catalogService.store.path
    bc.push({ url: generatePath(routes.catalogList, { type }), title: texts.titleCatalogList })
    bc.push({ url: generatePath(routes.catalogItemList, { type, id: config.id, parentId: '0' }), title: config.name })
    for (const item of path) {
      bc.push({ url: generatePath(routes.catalogItemList, { type, id: config.id, parentId: `${item.id}` }), title: item.name })
    }
  }

  if (page === 'catalog_item_detail') {
    const config = catalogService.store.configItemDetails
    const path = catalogService.store.path
    const item = catalogService.store.itemDetails
    bc.push({ url: generatePath(routes.catalogList, { type }), title: texts.titleCatalogList })
    bc.push({ url: generatePath(routes.catalogItemList, { type, id: config.id, parentId: '0' }), title: config.name })
    for (const item of path) {
      bc.push({ url: generatePath(routes.catalogItemList, { type, id: config.id, parentId: `${item.id}` }), title: item.name })
    }
    bc.push({ title: isCreate ? t.catalog.createEl : item.name })
  }

  useBreadcrumbs(bc)
}

export { useCatalog }
